import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Clock3, UserCircle2 } from "lucide-react";
import { getAllBlogPosts, getBlogPostBySlug } from "../data/blogPosts";
import useSEO from "../hooks/useSEO";

const formatDate = (dateValue) =>
  new Date(dateValue).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = useMemo(() => getBlogPostBySlug(slug), [slug]);

  const relatedPosts = useMemo(() => {
    if (!post) {
      return [];
    }

    const allPosts = getAllBlogPosts().filter((item) => item.slug !== post.slug);
    return allPosts
      .map((item) => {
        const score = item.tags.filter((tag) => post.tags.includes(tag)).length;
        return { ...item, score };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  }, [post]);

  useSEO(
    post
      ? {
          title: post.title,
          description: post.seoDescription,
          keywords: post.keywords,
          canonicalPath: `/blog/${post.slug}`,
          openGraphImage: post.coverImage,
          structuredData: {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BlogPosting",
                headline: post.title,
                description: post.seoDescription,
                image: `${window.location.origin}${post.coverImage}`,
                datePublished: post.publishedAt,
                dateModified: post.updatedAt,
                author: {
                  "@type": "Organization",
                  name: post.author,
                },
                publisher: {
                  "@type": "Organization",
                  name: "VIVID H2O Solutions",
                },
                mainEntityOfPage: {
                  "@type": "WebPage",
                  "@id": `${window.location.origin}/blog/${post.slug}`,
                },
              },
              ...(post.faq && post.faq.length
                ? [
                    {
                      "@type": "FAQPage",
                      mainEntity: post.faq.map((item) => ({
                        "@type": "Question",
                        name: item.question,
                        acceptedAnswer: {
                          "@type": "Answer",
                          text: item.answer,
                        },
                      })),
                    },
                  ]
                : []),
            ],
          },
        }
      : {
          title: "Blog Post Not Found",
          description: "The requested blog post was not found.",
          canonicalPath: "/blog",
        },
  );

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl shadow-sm p-8 text-center max-w-xl w-full">
          <h1 className="font-heading font-bold text-2xl text-gray-900 mb-3">Post not found</h1>
          <p className="text-gray-600 mb-5">
            The blog article you are looking for is unavailable or has moved.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#0d47a1] font-medium"
          >
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="blog-post-page">
      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#0d47a1] font-medium mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>

          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="aspect-[16/7] overflow-hidden bg-gray-100">
              <img src={post.coverImage} alt={post.title} className="w-full h-full object-cover" />
            </div>

            <div className="p-6 md:p-10">
              <h1 className="font-heading font-bold text-3xl md:text-4xl text-gray-900 mb-4">
                {post.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-8">
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="w-4 h-4" /> {formatDate(post.publishedAt)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock3 className="w-4 h-4" /> {post.readTime}
                </span>
                <span className="inline-flex items-center gap-1">
                  <UserCircle2 className="w-4 h-4" /> {post.author}
                </span>
              </div>

              <div className="prose prose-gray max-w-none">
                {post.content.map((section) => (
                  <section key={section.heading} className="mb-8">
                    <h2 className="font-heading font-semibold text-2xl text-gray-900 mb-3">
                      {section.heading}
                    </h2>
                    {section.paragraphs.map((paragraph, index) => (
                      <p key={index} className="text-gray-700 leading-7 mb-3">
                        {paragraph}
                      </p>
                    ))}
                  </section>
                ))}
              </div>

              <div className="border-t border-gray-100 pt-6 mt-10">
                <h3 className="font-heading font-semibold text-lg mb-3 text-gray-900">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs font-medium px-2 py-1 rounded-full bg-blue-50 text-[#0d47a1]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {post.recommendedLinks && post.recommendedLinks.length > 0 && (
                <div className="border-t border-gray-100 pt-6 mt-8">
                  <h3 className="font-heading font-semibold text-lg mb-3 text-gray-900">
                    Continue Reading
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {post.recommendedLinks.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="inline-flex items-center rounded-full bg-blue-50 text-[#0d47a1] px-4 py-2 text-sm font-medium hover:bg-blue-100"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {post.faq && post.faq.length > 0 && (
                <section className="border-t border-gray-100 pt-6 mt-8" aria-label="Frequently asked questions">
                  <h3 className="font-heading font-semibold text-lg mb-4 text-gray-900">
                    Frequently Asked Questions
                  </h3>
                  <div className="space-y-4">
                    {post.faq.map((item) => (
                      <div key={item.question} className="rounded-xl border border-gray-100 p-4 bg-gray-50">
                        <h4 className="font-semibold text-gray-900 mb-2">{item.question}</h4>
                        <p className="text-gray-700 text-sm leading-6">{item.answer}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>

          {relatedPosts.length > 0 && (
            <section className="mt-10">
              <h3 className="font-heading font-bold text-2xl text-gray-900 mb-4">Related Articles</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {relatedPosts.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/blog/${item.slug}`}
                    className="bg-white rounded-xl shadow-sm p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="text-xs text-gray-500 mb-2">{formatDate(item.publishedAt)}</div>
                    <h4 className="font-heading font-semibold text-gray-900 line-clamp-2 mb-2">
                      {item.title}
                    </h4>
                    <p className="text-sm text-gray-600 line-clamp-2">{item.excerpt}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
